import { Role } from '@/Enums/Users/Role.enum';
import { IClientControlOption } from '@/Interfaces/ControlOption.interface';
import { IParsedWhitelabelWithRelations } from '@/Interfaces/Whitelabels/Whitelabel.interface';
import { isOrgAdminRole, isSuperAdminRole } from '@/helpers/roles';
import { useAppSelector } from '@/store/hooks';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import AdditionalAdvertiserControls from './AdditionalAdvertiserControls';
import AdditionalAgencyControls from './AdditionalAgencyControls';
import AdditionalClientControls from './AdditionalClientControls';
import AdditionalOrgAdminControls from './AdditionalOrgAdminControls';
import AdditionalWhitelabelAdminControls from './AdditionalWhitelabelAdminControls';

interface IProps {
  shouldUnregister?: boolean;
  userRole: Role;
  meRole: Role;
}

const AdditionalControls = ({ userRole, shouldUnregister = false, meRole }: IProps) => {
  const { whitelabels, orgs, clients } = useAppSelector(state => state.userManagement);
  const showOrgLevel = useMemo(() => isSuperAdminRole(meRole), [meRole]);
  const showWhitelabelLevel = useMemo(() => isSuperAdminRole(meRole) || isOrgAdminRole(meRole), [meRole]);
  const [selectedOrg, selectedWhitelabel, selectedClient]: [
    IParsedWhitelabelWithRelations,
    IParsedWhitelabelWithRelations,
    IClientControlOption,
  ] = useWatch({
    name: ['organization', 'whitelabel', 'client'],
  });

  const getClientOptions = useCallback(
    (useSelectedWhitelabel?: boolean): IClientControlOption[] => {
      if (!showWhitelabelLevel || !useSelectedWhitelabel) return clients;
      return selectedWhitelabel?.clients || [];
    },
    [clients, selectedWhitelabel?.clients, showWhitelabelLevel],
  );

  switch (+userRole) {
    case Role.ORG_ADMIN:
      if (!showOrgLevel) return;
      return <AdditionalOrgAdminControls organizations={orgs} shouldUnregister={shouldUnregister} />;

    case Role.WHITELABEL_ADMIN:
      if (!showWhitelabelLevel) return;
      return (
        <AdditionalWhitelabelAdminControls
          showOrgControl={showOrgLevel}
          shouldUnregister={shouldUnregister}
          whitelabels={(whitelabels as IParsedWhitelabelWithRelations[]).filter(wl => wl.parentId === selectedOrg?.id)}
          organizations={orgs}
          whitelabelDisabled={showOrgLevel && !selectedOrg?.id}
        />
      );
    case Role.CLIENT:
      return (
        <AdditionalClientControls
          shouldUnregister={shouldUnregister}
          showWhitelabelControl={showWhitelabelLevel}
          clients={getClientOptions(true)}
          whitelabels={[...orgs, ...whitelabels]}
        />
      );
    case Role.ADVERTISER: {
      return (
        <AdditionalAdvertiserControls
          clients={getClientOptions()}
          advertisers={selectedClient?.advertisers || []}
          shouldUnregister={shouldUnregister}
        />
      );
    }
    case Role.AGENCY: {
      return (
        <AdditionalAgencyControls
          clients={getClientOptions()}
          agencies={selectedClient?.agencies || []}
          shouldUnregister={shouldUnregister}
        />
      );
    }
    default:
      return null;
  }
};

export default AdditionalControls;
